import React from 'react'
import styled from 'styled-components'

// Components
import { RowAligned } from '../layout/LayoutAtoms'
import Text from '../Text'
import Icon from '../icon/Icon'
import IconContainer from '../icon/IconContainer'
import { RoundedCard } from '../card/GenericCard'

// Utils
import { useAppState } from '../../context/AppContext'

// Styles
import { fastTransition, shadows } from '../../styles/effects'
import { applyTextEllipsis } from '../../styles/typography'

// Types
import { IconType } from '../icon/IconType'
import { AnyColor } from '../../common/models/colors'

interface IRitualCardProps {
  title: string
  caption: string
  color: string
  icon: IconType
  onClick?: () => void
  children?: React.ReactNode
  className?: string
}

export const RitualCard: React.FC<IRitualCardProps> = ({
  title,
  caption,
  color,
  icon,
  onClick,
  children,
  className,
}) => (
  <StyledRoundedCard
    className={className}
    onClick={onClick}
    clickable={!!onClick}
  >
    <ClickRow>
      <StyledIconContainer
        padding={'12px'}
        size={20}
        type={icon}
        color={color}
      />
      <div>
        <Text textStyle="title" color="textDark">
          {title}
        </Text>
        <Text textStyle="caption" color="text72">
          {caption}
        </Text>
      </div>
      {onClick && <StyledIcon type="ChevronRight" size={16} />}
    </ClickRow>
    {children}
  </StyledRoundedCard>
)

export const StyledIcon = styled(Icon)`
  margin-left: auto;
`

// The card's frame, with its hover when clickable; for a ritual card with its own layout
export const StyledRoundedCard = styled(RoundedCard)<{ clickable?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.textLightest};

  ${({ clickable, theme }) =>
    clickable &&
    `
    cursor: pointer;
    box-shadow: ${shadows.depthTwo};
    transition: box-shadow ${fastTransition}, border-color ${fastTransition};

    &:hover {
      border-color: ${theme.colors.primary};
      box-shadow: ${shadows.depthThree};

      ${StyledIcon} {
        svg > path {
          fill: ${theme.colors.primary};
        }
      }
    }
  `}
`

const StyledIconContainer = styled(IconContainer)`
  border-radius: 8px;
`

const ClickRow = styled(RowAligned)`
  margin-bottom: 24px;

  ${StyledIcon} {
    svg > path {
      fill: ${({ theme }) => theme.colors.primaryFadeLight};
    }
  }
`

interface ICounterProps {
  className?: string
  count: string
  total: string
}

export const Counter: React.FC<ICounterProps> = ({
  className,
  count,
  total,
}) => (
  <CounterContainer className={className}>
    <Text color="primary" textStyle="displayM" marginRight={8}>
      {count}
    </Text>
    <Text color="text72" textStyle="displayS">
      {`/ ${total}`}
    </Text>
  </CounterContainer>
)

const CounterContainer = styled(RowAligned)`
  align-items: flex-end;
  justify-content: flex-start;

  > p:last-of-type {
    ${applyTextEllipsis()};
  }
`

interface IIconTextProps {
  className?: string
  icon: IconType
  texts: Array<{
    text: string
    color?: string
    bold?: boolean
    italic?: boolean
  }>
}

export const IconText: React.FC<IIconTextProps> = ({
  icon,
  texts,
  className,
}) => {
  const { theme } = useAppState()
  return (
    <IconTextContainer className={className}>
      <Icon size={16} type={icon} color={theme.colors.textLight} />
      <TextWrapper>
        {texts.map((text, index) => (
          <Text
            key={index}
            tag="label"
            textStyle={
              text.bold
                ? 'captionHeavy'
                : text.italic
                ? 'captionItalic'
                : 'caption'
            }
            color={(text.color || 'textLight') as AnyColor}
            marginRight={index < texts.length - 1 ? 4 : 0}
          >
            {text.text}
          </Text>
        ))}
      </TextWrapper>
    </IconTextContainer>
  )
}

const IconTextContainer = styled(RowAligned)``

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`
